import { flatten, intersection, isEqual, map, uniq } from 'lodash'
import PropTypes from 'prop-types' // eslint-disable-line no-unused-vars
import { PureComponent } from 'react'
import { connect } from 'react-redux'
import { addLanguageTags, fetchLanguageTags, removeLanguageTag } from 'shared/api/index'
import { STATUS_STYLE } from 'shared/components/message/flash-message'
import SuggestedTagWrapper from 'shared/components/suggested-tags'
import TaggingTips from 'shared/components/suggested-tags/tagging-tips'
import { deleteColor, dividerColor, giphyBlack, giphyGreen, giphyGrey, giphyIndigo } from 'shared/css/colors.css'
import { message } from 'shared/redux/message'
import { TAG_LIMIT } from 'shared/variables'
import styled, { css as styledCss } from 'styled-components'
import TextInput from '../../inputs/text'
import EditPanelContext from '../context'
import LanguageSelect from './language-support/language-select'
import css from './tags.css'

const getLanguageTags = async (gifIds, tagLanguage) => {
    const isMultipleGifs = gifIds.length > 1
    const tagsData = await fetchLanguageTags(gifIds, tagLanguage)

    if (tagsData.gifs) {
        const gifTags = tagsData.gifs.map(g => g.tags.map(t => t.text))
        const allTags = uniq(flatten(gifTags))
        const commonTags = gifTags.length !== gifIds.length ? [] : intersection(...gifTags)

        const r = allTags.map(tag => ({
            tag,
            isCommon: isMultipleGifs && commonTags.indexOf(tag) > -1,
        }))
        return r
    }

    return []
}

const TagLanguages = {
    EN: '🇬🇧  English (en)',
    KO: '🇰🇷  Korean (ko)',
    PT: '🇵🇹  Portuguese (pt)',
    RU: '🇷🇺  Russian (ru)',
    ES: '🇪🇸  Spanish (es)',
    JA: '🇯🇵  Japanese (ja)',
    AR: '🇸🇦  Arabic (ar)',
    BN: '🇧🇩  Bengali (bn)',
    ZH_CN: '🇨🇳  Chinese Simplified (zh-CN)',
    ZH_TW: '🇹🇼  Chinese Traditional (zh-TW)',
    CS: '🇨🇿  Czech (cs)',
    DA: '🇩🇰  Danish (da)',
    NL: '🇳🇱  Dutch (nl)',
    FA: '🇮🇷  Farsi (fa)',
    FI: '🇫🇮  Finnish (fi)',
    FR: '🇫🇷  French (fr)',
    DE: '🇩🇪  German (de)',
    IW: '🇮🇱  Hebrew (iw)',
    HI: '🇮🇳  Hindi (hi)',
    HU: '🇭🇺  Hungarian (hu)',
    ID: '🇮🇩  Indonesian (id)',
    IT: '🇮🇹  Italian (it)',
    MS: '🇲🇾  Malay (ms)',
    NO: '🇳🇴  Norwegian (no)',
    PL: '🇵🇱  Polish (pl)',
    RO: '🇷🇴  Romanian (ro)',
    SV: '🇸🇪  Swedish (sv)',
    TH: '🇹🇭  Thai (th)',
    TL: '🇵🇭  Tagalog (tl)',
    TR: '🇹🇷  Turkish (tr)',
    UK: '🇺🇦  Ukranian (uk)',
    VI: '🇻🇳  Vietnamese (vi)',
}

const TagLanguagesAlt = map(TagLanguages, (value, key) => {
    return { name: value, value: key }
})

const Tag = styled.div`
    background: ${dividerColor};
    color: ${giphyGrey};
    border: ${props => (props.isCommon ? `1px solid ${giphyIndigo}` : 'none')};
    border-radius: 18px;
    display: ${props => (props.bulkEditMode ? 'block' : 'inline-block')};
    font-style: italic;
    height: 36px;
    line-height: 36px;
    margin: ${props => (props.bulkEditMode ? '0 0 6px 0;' : '0 8px 6px 0')};
    text-transform: none;
    user-select: none;

    ${props =>
        (props.isCommon || !props.isMultipleGifs) &&
        props.permissions.feature_tags &&
        props.isTagFeaturable &&
        styledCss`
        background: ${props.isFeatured ? giphyGreen : dividerColor};
        color: ${props.isFeatured ? giphyBlack : giphyGrey};
        cursor: pointer;

        &:hover {
            background: ${props.isFeatured ? dividerColor : giphyGreen};
            color: ${props.isFeatured ? giphyGrey : giphyBlack};
        }
    `};
`

const TagsContainer = styled.div`
    margin-bottom: 20px;
`

const LanguageContainer = styled.div`
    display: inline-block;

    ${props =>
        props.bulkEditMode
            ? styledCss`
        width: 100%;
        `
            : styledCss`
            width: 242px;
        `};
`

/**
 * Edit Panel - Tags: Controls the tag input
 * and pills from the GIF Edit Panel. This
 * allows user to add tags as well as remove
 * any pre-existing tags.
 *
 * @class EditPanelTags
 */
class EditPanelTags extends PureComponent {
    static contextType = EditPanelContext
    static propTypes = {
        className: PropTypes.string,
        user: PropTypes.object,
        permissions: PropTypes.object,
        gifs: PropTypes.array,
        tags: PropTypes.array,
        tagCounts: PropTypes.object,
        onAddTags: PropTypes.func.isRequired,
        onRemoveTag: PropTypes.func.isRequired,
        onRemoveAllTags: PropTypes.func,
        onFeatureTag: PropTypes.func,
        onUnfeatureTag: PropTypes.func,
    }
    static defaultProps = {
        user: {},
        permissions: {},
        gifs: [],
        tags: [],
        tagCounts: {},
        placeholder: 'Add a tag',
        onFeatureTag: () => {},
        onUnfeatureTag: () => {},
    }

    state = {
        tagLanguage: 'EN',
        tags: [],
        lastEnteredTags: [],
    }

    constructor(props) {
        super(props)
        // Default to first two tags for suggestions if tags already exist.
        this.state.lastEnteredTags = props.tags.slice(0, 2).map(i => i.tag)
    }

    componentDidMount() {
        this.updateTags()
    }

    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.gifs, prevProps.gifs)) {
            this.updateTags()
            this.setState({
                lastEnteredTags: this.props.tags.slice(0, 2).map(i => i.tag),
            })
        }
    }

    async updateTags() {
        const { tagLanguage } = this.state
        const { gifs, tags: englishTags } = this.props

        let tags = []

        if (tagLanguage === 'EN') {
            tags = englishTags
        } else {
            const gifIds = gifs.map(g => g.id)

            try {
                tags = await getLanguageTags(gifIds, tagLanguage)
            } catch (error) {
                message(`There was a problem fetching ${tagLanguage} tags.`, STATUS_STYLE.ERROR)
            }
        }

        this.setState({ tags: tags })
    }

    onRemove = tag => {
        const { gifs, onRemoveTag } = this.props
        const { tagLanguage } = this.state

        if (tagLanguage === 'EN') {
            onRemoveTag(tag)
        } else {
            const gifIds = gifs.map(g => g.id)

            for (const id of gifIds) {
                removeLanguageTag(id, tag, tagLanguage).then(() => this.updateTags())
            }
        }
    }

    onAdd = (newTag, isSuggested = false) => {
        const { tagLanguage, tags } = this.state
        const { gifs, onAddTags, message, user } = this.props

        const tagExists = tags.find(t => t.tag === newTag && t.isCommon)

        if (tagExists) {
            message('That tag already exists!', STATUS_STYLE.ERROR)
            return
        }

        let newTags = newTag.split(',')
        newTags = uniq(newTags.map(tag => tag.replace(/#/g, '').trim()))

        if (newTags.length + tags.length > TAG_LIMIT && !user.isStaff) {
            message('Sorry! Max number of tags reached.', STATUS_STYLE.ERROR)
            return
        }

        if (tagLanguage === 'EN') {
            onAddTags(newTags)
        } else {
            const gifIds = gifs.map(g => g.id)
            for (const id of gifIds) {
                addLanguageTags(id, newTags, tagLanguage).then(() => this.updateTags())
            }
        }
        if (!isSuggested) {
            this.setState({ lastEnteredTags: newTags })
        }
    }

    onTagLanguageChange(lang) {
        this.setState({ tagLanguage: lang || 'EN' }, this.updateTags)
    }

    render() {
        const {
            className,
            user,
            permissions,
            tagCounts,
            onFeatureTag,
            onUnfeatureTag,
            name,
            fixedWidth,
            placeholder,
            isMultipleGifs,
            gifs,
        } = this.props
        const { bulkEditMode } = this.context
        const { tags, tagLanguage, lastEnteredTags } = this.state
        const isTagFeaturable = tagLanguage === 'EN'
        const showSuggestedTags = user.isVerified || user.isStaff
        const gifIds = gifs.map(x => x.id)
        return (
            <div className={className}>
                {name && <span className={css.name}>{name}</span>}
                <div className={!bulkEditMode ? css.headerFluid : css.header}>
                    {bulkEditMode && (
                        <div className={css.tagsHeader}>
                            <span>Tags</span>
                            <TaggingTips isVertical={bulkEditMode} />
                        </div>
                    )}
                    <TextInput
                        className={!fixedWidth ? css.tagInputFluid : css.tagInput}
                        type="text"
                        autoSelect={true}
                        placeholder={placeholder}
                        buttonLabel={<i className={css.inputAdd} />}
                        onSubmit={this.onAdd}
                    />
                    {isMultipleGifs && (
                        <TextInput
                            className={!fixedWidth ? css.tagInputFluid : css.tagInput}
                            type="text"
                            autoSelect={true}
                            placeholder={'Remove a tag'}
                            buttonLabel={<i className={css.inputRemove} />}
                            buttonColor={deleteColor}
                            onSubmit={this.onRemove}
                        />
                    )}

                    {permissions.edit_language_tags && (
                        <LanguageContainer bulkEditMode={bulkEditMode}>
                            <LanguageSelect
                                options={TagLanguagesAlt}
                                defaultValue="EN"
                                onChange={value => this.onTagLanguageChange(value)}
                            ></LanguageSelect>
                        </LanguageContainer>
                    )}
                    {!bulkEditMode && <TaggingTips />}
                </div>
                <TagsContainer bulkEditMode={bulkEditMode}>
                    {tags &&
                        tags.map(({ tag, isCommon, isFeatured }) => (
                            <Tag
                                key={tag.tag || tag}
                                user={user}
                                isCommon={isCommon}
                                isFeatured={isFeatured}
                                permissions={permissions}
                                isMultipleGifs={isMultipleGifs}
                                isTagFeaturable={isTagFeaturable}
                                onClick={() => {
                                    if (isTagFeaturable && permissions.feature_tags) {
                                        if (isMultipleGifs && !isCommon) {
                                            return
                                        }
                                        isFeatured ? onUnfeatureTag(tag) : onFeatureTag(tag)
                                    }
                                }}
                                bulkEditMode={bulkEditMode}
                            >
                                <span className={css.label}>
                                    {tag.tag || tag}{' '}
                                    {tagCounts[tag] && (
                                        <span className={css.tagCount}>{tagCounts[tag].toLocaleString()}</span>
                                    )}
                                </span>
                                <i
                                    className={css.remove}
                                    onClick={e => {
                                        e.stopPropagation()
                                        this.onRemove(tag)
                                    }}
                                />
                            </Tag>
                        ))}
                </TagsContainer>
                {showSuggestedTags && (
                    <SuggestedTagWrapper
                        currentTags={tags}
                        lastEnteredTags={lastEnteredTags}
                        endpoint="edit-panel"
                        gifIds={gifIds}
                        addSugTag={tag => this.onAdd(tag, true)}
                        isVertical={bulkEditMode}
                        userName={user.username}
                    />
                )}
            </div>
        )
    }
}

export default connect(undefined, { message })(EditPanelTags)
